import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { LocalStorageHelpers } from "@ai360/core";

import { messages } from "../i18n-messages";
import * as actions from "../actions";
import * as selectors from "../selectors";
import { useSsoRedirect } from "../use-sso-redirect";
import { useAppSelector, useAppDispatch } from "~/store/hooks";
import { Button, TextInput } from "~/core";
import { isValidEmail } from "./recover-password";

const { LoginState } = actions;

export interface IPasswordEntryProps {
    onError: (error: any) => void;
    doVersionCheck: () => Promise<boolean>;
}

export const PasswordEntry = (props: IPasswordEntryProps): JSX.Element => {
    const { doVersionCheck, onError } = props;
    const { formatMessage } = useIntl();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const redirectIfSsoConnected = useSsoRedirect();

    const error = useAppSelector(selectors.getError);
    const loginEmail = useAppSelector(selectors.getLoginEmail);

    const loginPassword = useAppSelector(selectors.getLoginPassword);

    // Editable so a mistyped address can be fixed here instead of restarting the flow.
    const [email, setEmail] = useState<string>(
        loginEmail || LocalStorageHelpers.get(LocalStorageHelpers.USERNAME) || ""
    );
    const [password, setPassword] = useState<string>(loginPassword || "");
    const [txtPassword, setTxtPassword] = useState<Record<string, any>>(null);

    function _clearPassword() {
        setPassword("");
        if (txtPassword) {
            txtPassword.input.focus();
        }
    }

    function _reportError(err) {
        onError(err);
        dispatch(actions.setProcessing(false));
    }

    function _handleError(err) {
        _clearPassword();
        _reportError(err);
    }

    async function login(): Promise<void> {
        dispatch(actions.setProcessing(true));
        dispatch(actions.setError(null));
        const submittedEmail = email.replaceAll(" ", "");
        if (submittedEmail.length === 0) {
            _reportError(formatMessage(messages.usernameRequired));
            return;
        }
        if (!isValidEmail(submittedEmail)) {
            _reportError(formatMessage(messages.validEmail));
            return;
        }
        if (submittedEmail !== loginEmail) {
            // The email step only checked SSO status for the original address, so a
            // corrected one must be checked before it is used for a password login.
            if (await redirectIfSsoConnected(submittedEmail)) {
                return; // redirecting; keep spinner up
            }
            if (LocalStorageHelpers.get(LocalStorageHelpers.REMEMBER_ME)) {
                LocalStorageHelpers.set(LocalStorageHelpers.USERNAME, submittedEmail);
            }
            dispatch(actions.setLoginEmail(submittedEmail));
        }
        if (password.length === 0) {
            _handleError(formatMessage(messages.passwordRequired));
            return;
        }
        if (props.doVersionCheck) {
            props.doVersionCheck().then((isRefreshing) => {
                if (!isRefreshing) {
                    dispatch(actions.login(submittedEmail, password, history));
                }
            });
        } else {
            dispatch(actions.login(submittedEmail, password, history));
        }
    }

    function recoverPassword(evt) {
        evt.preventDefault();
        dispatch(actions.setError(null));
        dispatch(actions.setLoginState(LoginState.RECOVER_PASSWORD));
    }

    useEffect(() => {
        if (error) {
            _clearPassword();
            dispatch(actions.setProcessing(false));
        }
    }, [error]);

    useEffect(() => {
        if (loginPassword) {
            dispatch(actions.setProcessing(true));
            dispatch(actions.setError(null));
            dispatch(actions.setLoginPassword(""));
            if (doVersionCheck) {
                doVersionCheck().then((isRefreshing) => {
                    if (!isRefreshing) {
                        dispatch(actions.login(email, loginPassword, history));
                    }
                });
            } else {
                dispatch(actions.login(email, loginPassword, history));
            }
        }
    }, [dispatch, doVersionCheck, email, history, loginPassword]);

    const inputFormProps = {
        onKeyDown: (e) => {
            if (e.key === "Enter") {
                login();
            }
        },
    };
    return (
        <div>
            <div className="login-input-container">
                <TextInput
                    tabIndex={1}
                    value={email}
                    autoComplete="username"
                    name="username"
                    placeholderText={formatMessage(messages.usernameLbl)}
                    testId="password-entry-username-input"
                    {...inputFormProps}
                    //removing whitespaces as you type does not trigger re-render on state change
                    onChange={(value) => setEmail(value)}
                    //onBlur does not pass in the text input value, using the value stored by onChange
                    onBlur={() => setEmail(email.replaceAll(" ", ""))}
                />
                <TextInput
                    tabIndex={2}
                    value={password}
                    password
                    autoFocus
                    autoComplete="current-password"
                    name="password"
                    placeholderText={formatMessage(messages.passwordLbl)}
                    {...inputFormProps}
                    onChange={(p) => setPassword(p)}
                    ref={(input) => {
                        setTxtPassword(input);
                    }}
                    testId="password-entry-password-input"
                />
                <div className="login-options-div">
                    <div className="login-forgot-password">
                        <a
                            tabIndex={3}
                            href="#Recover Password"
                            onClick={(evt) => recoverPassword(evt)}
                        >
                            {formatMessage(messages.forgotPasswordLnk)}
                        </a>
                    </div>
                </div>
            </div>
            <div className="login-center">
                <Button
                    tabIndex={4}
                    className="login-btn"
                    type="login"
                    {...inputFormProps}
                    onClick={() => login()}
                    testId="password-entry-login-button"
                />
            </div>
        </div>
    );
};

export default PasswordEntry;
